import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import type { DailySales, HourlySales, ChannelBreakdown, TopProduct, PeakDay, PeakHour } from '../../lib/metrics-hooks';
// ─── Custom Tooltip ────────────────────────────────────────
function ChartTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm" data-id="element-865">
      <p className="font-medium text-gray-300 mb-1" data-id="element-866">{label}</p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-867">
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color
      }} data-id="element-868" />
          <span className="text-gray-400" data-id="element-869">{entry.name}:</span>
          <span className="font-semibold" data-id="element-870">
            {isCurrency ? formatCurrency(entry.value) : entry.value}
          </span>
        </p>)}
    </div>;
}
// ─── Daily Sales Chart ─────────────────────────────────────
interface DailySalesChartProps {
  data: DailySales[];
  peakDays: PeakDay[];
}
export function DailySalesChart({
  data,
  peakDays
}: DailySalesChartProps) {
  const [metric, setMetric] = useState<'revenue' | 'orders'>('revenue');
  const peakDates = new Set(peakDays.map(p => p.date));
  const chartData = data.map(d => ({
    ...d,
    isPeak: peakDates.has(d.date),
    displayLabel: `${d.dayOfWeek} ${d.label}`
  }));
  return <Card data-id="element-871">
      <CardHeader data-id="element-872">
        <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-873">
          <CardTitle data-id="element-874">Ventas por Día</CardTitle>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" data-id="element-875">
            <button onClick={() => setMetric('revenue')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${metric === 'revenue' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-876">
              Ingresos
            </button>
            <button onClick={() => setMetric('orders')} className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${metric === 'orders' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-877">
              Órdenes
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent data-id="element-878">
        {data.length === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-879">
            No hay datos para el período seleccionado
          </div> : <div className="h-72" data-id="element-880">
            <ResponsiveContainer width="100%" height="100%" data-id="element-881">
              <AreaChart data={chartData} margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0
          }} data-id="element-882">
                <defs data-id="element-883">
                  <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-884">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} data-id="element-885" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-886" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} data-id="element-887" />
                <XAxis dataKey="label" tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={{
              stroke: '#e5e7eb'
            }} interval={data.length > 15 ? Math.floor(data.length / 10) : 0} data-id="element-888" />
                <YAxis tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={false} tickFormatter={v => metric === 'revenue' ? `₡${(v / 1000).toFixed(0)}k` : String(v)} data-id="element-889" />
                <Tooltip content={<ChartTooltip isCurrency={metric === 'revenue'} data-id="element-891" />} data-id="element-890" />
                <Area type="monotone" dataKey={metric} name={metric === 'revenue' ? 'Ingresos' : 'Órdenes'} stroke="#10b981" strokeWidth={2} fill="url(#dailyGradient)" dot={(props: any) => {
              const {
                cx,
                cy,
                payload
              } = props;
              if (payload.isPeak) {
                return <circle key={`peak-${payload.date}`} cx={cx} cy={cy} r={5} fill="#ef4444" stroke="#fff" strokeWidth={2} data-id="element-893" />;
              }
              return <circle key={`dot-${payload.date}`} cx={cx} cy={cy} r={0} data-id="element-894" />;
            }} data-id="element-892" />
              </AreaChart>
            </ResponsiveContainer>
          </div>}
        {peakDays.length > 0 && <div className="mt-3 flex items-center gap-4 text-xs text-gray-500" data-id="element-895">
            <span className="flex items-center gap-1.5" data-id="element-896">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500" data-id="element-897" />
              Días pico (mayor volumen)
            </span>
          </div>}
      </CardContent>
    </Card>;
}
// ─── Hourly Sales Chart ────────────────────────────────────
interface HourlySalesChartProps {
  data: HourlySales[];
  peakHours: PeakHour[];
}
export function HourlySalesChart({
  data,
  peakHours
}: HourlySalesChartProps) {
  const peakHourSet = new Set(peakHours.map(p => p.hour));
  // Only show hours with activity + 1 hour padding
  const activeHours = data.filter(h => h.orders > 0);
  const minHour = activeHours.length > 0 ? Math.max(0, activeHours[0].hour - 1) : 6;
  const maxHour = activeHours.length > 0 ? Math.min(23, activeHours[activeHours.length - 1].hour + 1) : 22;
  const filteredData = data.filter(h => h.hour >= minHour && h.hour <= maxHour);
  return <Card data-id="element-898">
      <CardHeader data-id="element-899">
        <CardTitle data-id="element-900">Distribución por Hora</CardTitle>
      </CardHeader>
      <CardContent data-id="element-901">
        {activeHours.length === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-902">
            No hay datos para el período seleccionado
          </div> : <div className="h-72" data-id="element-903">
            <ResponsiveContainer width="100%" height="100%" data-id="element-904">
              <BarChart data={filteredData} margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0
          }} data-id="element-905">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} data-id="element-906" />
                <XAxis dataKey="label" tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={{
              stroke: '#e5e7eb'
            }} data-id="element-907" />
                <YAxis tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={false} data-id="element-908" />
                <Tooltip content={<ChartTooltip isCurrency={false} data-id="element-910" />} data-id="element-909" />
                <Bar dataKey="orders" name="Órdenes" radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-911">
                  {filteredData.map(entry => <Cell key={`cell-${entry.hour}`} fill={peakHourSet.has(entry.hour) ? '#f59e0b' : '#6366f1'} opacity={peakHourSet.has(entry.hour) ? 1 : 0.7} data-id="element-912" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>}
        {peakHours.length > 0 && <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 flex-wrap" data-id="element-913">
            <span className="flex items-center gap-1.5" data-id="element-914">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" data-id="element-915" />
              Horas pico
            </span>
            <span className="flex items-center gap-1.5" data-id="element-916">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 opacity-70" data-id="element-917" />
              Otras horas
            </span>
          </div>}
      </CardContent>
    </Card>;
}
// ─── Channel Pie Chart ─────────────────────────────────────
interface ChannelChartProps {
  data: ChannelBreakdown;
}
const CHANNEL_COLORS = ['#10b981', '#3b82f6', '#9ca3af'];
const CHANNEL_LABELS: Record<string, string> = {
  qr: 'QR (Autoservicio)',
  waiter: 'Mesero',
  other: 'Otros canales'
};
export function ChannelPieChart({
  data
}: ChannelChartProps) {
  const total = data.qr + data.waiter + data.other;
  const pieData = [{
    name: CHANNEL_LABELS.qr,
    value: data.qr,
    revenue: data.qrRevenue,
    color: CHANNEL_COLORS[0]
  }, {
    name: CHANNEL_LABELS.waiter,
    value: data.waiter,
    revenue: data.waiterRevenue,
    color: CHANNEL_COLORS[1]
  }, {
    name: CHANNEL_LABELS.other,
    value: data.other,
    revenue: data.otherRevenue,
    color: CHANNEL_COLORS[2]
  }].filter(d => d.value > 0);
  return <Card data-id="element-918">
      <CardHeader data-id="element-919">
        <CardTitle data-id="element-920">QR vs Mesero</CardTitle>
      </CardHeader>
      <CardContent data-id="element-921">
        {total === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-922">
            No hay datos para el período seleccionado
          </div> : <div className="flex flex-col items-center" data-id="element-923">
            <div className="h-56 w-full max-w-xs" data-id="element-924">
              <ResponsiveContainer width="100%" height="100%" data-id="element-925">
                <PieChart data-id="element-926">
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-927">
                    {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-928" />)}
                  </Pie>
                  <Tooltip content={({
                active,
                payload
              }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                const pct = total > 0 ? Math.round(d.value / total * 100) : 0;
                return <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm" data-id="element-930">
                          <p className="font-medium" data-id="element-931">{d.name}</p>
                          <p className="text-gray-300" data-id="element-932">
                            {d.value} pedidos ({pct}%)
                          </p>
                          <p className="text-gray-300" data-id="element-933">
                            {formatCurrency(d.revenue)}
                          </p>
                        </div>;
              }} data-id="element-929" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex flex-col gap-2 mt-2 w-full" data-id="element-934">
              {pieData.map(entry => {
            const pct = total > 0 ? Math.round(entry.value / total * 100) : 0;
            return <div key={entry.name} className="flex items-center justify-between px-2" data-id="element-935">
                    <div className="flex items-center gap-2" data-id="element-936">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{
                  backgroundColor: entry.color
                }} data-id="element-937" />
                      <span className="text-sm text-gray-700" data-id="element-938">
                        {entry.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-939">
                      <span className="text-sm font-semibold text-gray-900" data-id="element-940">
                        {entry.value}
                      </span>
                      <span className="text-xs text-gray-400 w-10 text-right" data-id="element-941">
                        {pct}%
                      </span>
                    </div>
                  </div>;
          })}
            </div>
          </div>}
      </CardContent>
    </Card>;
}
// ─── Top Products Bar Chart ────────────────────────────────
interface TopProductsChartProps {
  data: TopProduct[];
}
export function TopProductsChart({
  data
}: TopProductsChartProps) {
  const chartData = data.map(p => ({
    ...p,
    shortName: p.name.length > 20 ? p.name.slice(0, 18) + '…' : p.name
  }));
  return <Card data-id="element-942">
      <CardHeader data-id="element-943">
        <CardTitle data-id="element-944">Productos Más Vendidos</CardTitle>
      </CardHeader>
      <CardContent data-id="element-945">
        {data.length === 0 ? <div className="h-64 flex items-center justify-center text-gray-400 text-sm" data-id="element-946">
            No hay datos para el período seleccionado
          </div> : <div className="h-72" data-id="element-947">
            <ResponsiveContainer width="100%" height="100%" data-id="element-948">
              <BarChart data={chartData} layout="vertical" margin={{
            top: 0,
            right: 8,
            left: 0,
            bottom: 0
          }} data-id="element-949">
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} data-id="element-950" />
                <XAxis type="number" tick={{
              fontSize: 11,
              fill: '#9ca3af'
            }} tickLine={false} axisLine={false} data-id="element-951" />
                <YAxis type="category" dataKey="shortName" tick={{
              fontSize: 11,
              fill: '#6b7280'
            }} tickLine={false} axisLine={false} width={120} data-id="element-952" />
                <Tooltip content={({
              active,
              payload
            }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm" data-id="element-954">
                        <p className="font-medium" data-id="element-955">{d.name}</p>
                        <p className="text-gray-300" data-id="element-956">{d.quantity} unidades</p>
                        <p className="text-gray-300" data-id="element-957">
                          {formatCurrency(d.revenue)}
                        </p>
                      </div>;
            }} data-id="element-953" />
                <Bar dataKey="quantity" name="Cantidad" fill="#8b5cf6" radius={[0, 4, 4, 0]} maxBarSize={28} opacity={0.85} data-id="element-958" />
              </BarChart>
            </ResponsiveContainer>
          </div>}
      </CardContent>
    </Card>;
}